/**
 * Navigation component - Sticky navigation with smooth scrolling
 * Constitutional requirement: React with regular JavaScript
 */

import { useState, useEffect } from 'react'
import { Calculator, BookOpen, TrendingUp, HelpCircle, Info, Shield, Menu, X, Mail } from 'lucide-react'
import '../navigation.css'

const Navigation = () => {
  const [activeSection, setActiveSection] = useState('calculator')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const navItems = [
    { id: 'calculator', label: 'Calculator', icon: Calculator },
    { id: 'how-it-works', label: 'How It Works', icon: BookOpen },
    { id: 'education', label: 'Education', icon: TrendingUp },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
    { id: 'about', label: 'About', icon: Info },
    { id: 'contact', label: 'Contact', icon: Mail },
    { id: 'privacy', label: 'Privacy', icon: Shield }
  ]

  // Track scroll position for active section highlighting
  useEffect(() => {
    const handleScroll = () => {
      // Add background to nav when scrolled
      setIsScrolled(window.scrollY > 100)

      // Determine active section based on scroll position
      const sections = navItems.map(item => {
        const element = document.getElementById(item.id)
        if (element) {
          const rect = element.getBoundingClientRect()
          return {
            id: item.id,
            top: rect.top,
            bottom: rect.bottom
          }
        }
        return null
      }).filter(Boolean)

      // Find the section that's most visible in viewport
      const visible = sections.find(section => 
        section.top <= 150 && section.bottom > 150
      )

      if (visible) {
        setActiveSection(visible.id)
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const offset = 80 // Account for sticky nav height
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
    setIsMenuOpen(false) // Close mobile menu after click
  }

  return (
    <nav className={`main-navigation ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        
        {/* Logo/Brand */}
        <div className="nav-brand" onClick={() => scrollToSection('calculator')}>
          <Calculator size={24} color="#ff6b35" />
          <span className="brand-text">Zero Debt Journey</span>
        </div>

        {/* Desktop Navigation */}
        <ul className="nav-menu desktop">
          {navItems.map(item => {
            const Icon = item.icon
            return (
              <li key={item.id}>
                <button
                  className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(item.id)}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </button>
              </li>
            )
          })}
        </ul>

        {/* Mobile Menu Toggle */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="mobile-menu-overlay" onClick={() => setIsMenuOpen(false)}>
            <ul className="nav-menu mobile" onClick={(e) => e.stopPropagation()}>
              {navItems.map(item => {
                const Icon = item.icon
                return (
                  <li key={item.id}>
                    <button
                      className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
                      onClick={() => scrollToSection(item.id)}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

      </div>
    </nav>
  )
}

export default Navigation
